import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    BellSimpleIcon,
    WhatsappLogoIcon,
    WebhooksLogoIcon,
} from '@phosphor-icons/react';
import { ActionConfig, AlertRuleActionType } from '@/types/alert';
import { ALERT_DEFAULT_CONFIDENCE_THRESHOLD } from '@/constants/alert';
import { BetaTag } from '@/components/BetaTag';
import { Button } from '@/components/ui/Button';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { isActionConfigured } from '../validation';
import { AlertActionCard } from './AlertActionCard';
import { ActionConfigPopover } from './ActionConfigPopover';

const ACTION_CONFIGS = [
    {
        type: AlertRuleActionType.APP_NOTIFICATION,
        icon: BellSimpleIcon,
        titleKey: 'form.actionConfig.appNotification.title',
        subtitleKey: 'form.actionConfig.appNotification.subtitle',
        enabled: true,
        beta: false,
    },
    {
        type: AlertRuleActionType.WHATSAPP,
        icon: WhatsappLogoIcon,
        titleKey: 'form.actionConfig.whatsapp.title',
        subtitleKey: 'form.actionConfig.whatsapp.subtitle',
        enabled: false,
        beta: false,
    },
    {
        type: AlertRuleActionType.WEBHOOK,
        icon: WebhooksLogoIcon,
        titleKey: 'form.actionConfig.webhooks.title',
        subtitleKey: 'form.actionConfig.webhooks.subtitle',
        enabled: true,
        beta: true,
    },
] as const;

interface ActionConfigurationStepProps {
    actions: ActionConfig[];
    editMode?: boolean;
    submitting?: boolean;
    error?: string;
    onActionsChange: (actions: ActionConfig[]) => void;
    onSubmit: () => void;
    onPreviewConfidenceThreshold?: (threshold: number | null) => void;
}

const getWebhookHost = (
    action: ActionConfig | undefined,
): string | undefined => {
    if (!action?.webhook?.url) return undefined;
    try {
        return new URL(action.webhook.url).host;
    } catch {
        return undefined;
    }
};

const getRecipientCount = (action: ActionConfig | undefined): number => {
    if (!action) return 0;

    return action.recipients
        ? action.recipients.filter((r) => r.phone && r.phone.trim() !== '')
              .length
        : action.recipient?.phone && action.recipient.phone.trim() !== ''
          ? 1
          : 0;
};

export function ActionConfigurationStep({
    actions,
    editMode = false,
    submitting = false,
    error,
    onActionsChange,
    onSubmit,
    onPreviewConfidenceThreshold,
}: ActionConfigurationStepProps) {
    const { t } = useTranslation(I18nNamespace.ALERT_RULES);
    const [editingActionType, setEditingActionType] =
        useState<AlertRuleActionType | null>(null);

    const handleActionClick = (type: AlertRuleActionType) => {
        const existingAction = actions.find((a) => a.type === type);

        if (!existingAction) {
            const newAction: ActionConfig = {
                type,
                confidenceThreshold: ALERT_DEFAULT_CONFIDENCE_THRESHOLD,
            };
            onActionsChange([...actions, newAction]);
        }

        setEditingActionType(type);
    };

    const handleRemoveAction = (type: AlertRuleActionType) => {
        const existingActionIndex = actions.findIndex((a) => a.type === type);

        if (existingActionIndex >= 0) {
            const newActions = [...actions];
            newActions.splice(existingActionIndex, 1);
            onActionsChange(newActions);
            setEditingActionType(null);
        }
    };

    const handleSaveAction = (updatedAction: ActionConfig) => {
        const existingIndex = actions.findIndex(
            (a) => a.type === updatedAction.type,
        );

        if (existingIndex >= 0) {
            const newActions = [...actions];
            newActions[existingIndex] = updatedAction;
            onActionsChange(newActions);
        } else {
            onActionsChange([...actions, updatedAction]);
        }
    };

    return (
        <div className="flex flex-col gap-4">
            <div className="grid grid-cols-2 gap-4">
                {ACTION_CONFIGS.map((actionConfig) => {
                    const selectedAction = actions.find(
                        (a) => a.type === actionConfig.type,
                    );
                    const isEditing = editingActionType === actionConfig.type;
                    const recipientCount =
                        actionConfig.type === AlertRuleActionType.WHATSAPP
                            ? getRecipientCount(selectedAction)
                            : 0;
                    const chipLabel =
                        actionConfig.type === AlertRuleActionType.WEBHOOK
                            ? getWebhookHost(selectedAction)
                            : recipientCount > 0
                              ? t('form.actionConfig.recipientCount', {
                                    count: recipientCount,
                                })
                              : undefined;

                    return (
                        <ActionConfigPopover
                            key={actionConfig.type}
                            action={
                                selectedAction || {
                                    type: actionConfig.type,
                                    confidenceThreshold:
                                        ALERT_DEFAULT_CONFIDENCE_THRESHOLD,
                                }
                            }
                            isOpen={isEditing}
                            onOpenChange={(open) => {
                                if (!open) {
                                    setEditingActionType(null);
                                }
                            }}
                            onSave={handleSaveAction}
                            onPreviewConfidenceThreshold={
                                onPreviewConfidenceThreshold
                            }
                        >
                            <div className="w-full">
                                <AlertActionCard
                                    icon={actionConfig.icon}
                                    title={t(actionConfig.titleKey)}
                                    subtitle={t(actionConfig.subtitleKey)}
                                    enabled={actionConfig.enabled}
                                    isSelected={!!selectedAction}
                                    onClick={() =>
                                        actionConfig.enabled &&
                                        handleActionClick(actionConfig.type)
                                    }
                                    onRemove={() =>
                                        handleRemoveAction(actionConfig.type)
                                    }
                                    chipLabel={chipLabel}
                                    titleBadge={
                                        actionConfig.beta && <BetaTag />
                                    }
                                />
                            </div>
                        </ActionConfigPopover>
                    );
                })}
            </div>

            {error && <p className="text-sm text-red-400">{error}</p>}

            <div className="flex justify-end items-center">
                <Button
                    onClick={onSubmit}
                    disabled={
                        actions.length === 0 ||
                        submitting ||
                        !actions.every(isActionConfigured)
                    }
                >
                    {submitting
                        ? editMode
                            ? t('form.buttons.saving')
                            : t('form.buttons.creating')
                        : editMode
                          ? t('form.buttons.saveRule')
                          : t('form.buttons.createRule')}
                </Button>
            </div>
        </div>
    );
}
